import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  CreateTagRequestBody,
  LoadPredefinedTagsResponse,
  TaggableEntityType,
  TagListResponse,
  TagResponse,
  UpdateTagRequestBody,
} from "common";

const ADMIN_TAG_QUERY_KEY = "/api/admin/tag";

export const useAdminTagList = (
  entityType: TaggableEntityType
): UseQueryResult<TagListResponse, Error> => {
  return useQuery<TagListResponse, Error>({
    queryKey: [ADMIN_TAG_QUERY_KEY, entityType],
    queryFn: async () => {
      const res = await axios.get(ADMIN_TAG_QUERY_KEY, {
        params: { entityType },
      });
      return res.data;
    },
    retry: false,
  });
};

export const useCreateTag = (): UseMutationResult<
  TagResponse,
  ErrorNotification,
  CreateTagRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<TagResponse, ErrorNotification, CreateTagRequestBody>({
    mutationFn: async (payload: CreateTagRequestBody) => {
      const res = await axios.post(ADMIN_TAG_QUERY_KEY, payload);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      setNotification({
        id: "create-tag-success",
        message: `${variables.entityType.charAt(0).toUpperCase() + variables.entityType.slice(1)} Tag Created`,
        details: `The ${variables.entityType} tag has been created successfully.`,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [ADMIN_TAG_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-tag-error",
        message: "Failed to Create Tag",
        details: error?.response?.data?.message ?? "Failed to create tag.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateTag = (): UseMutationResult<
  TagResponse,
  ErrorNotification,
  { tagId: string } & UpdateTagRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    TagResponse,
    ErrorNotification,
    { tagId: string } & UpdateTagRequestBody
  >({
    mutationFn: async ({ tagId, ...payload }) => {
      const res = await axios.put(`${ADMIN_TAG_QUERY_KEY}/${tagId}`, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "update-tag-success",
        message: "Tag Updated",
        details: "The tag has been updated successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [ADMIN_TAG_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "update-tag-error",
        message: "Failed to Update Tag",
        details: error?.response?.data?.message ?? "Failed to update tag.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useLoadPredefinedTags = (): UseMutationResult<
  LoadPredefinedTagsResponse,
  ErrorNotification,
  { entityType: TaggableEntityType }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    LoadPredefinedTagsResponse,
    ErrorNotification,
    { entityType: TaggableEntityType }
  >({
    mutationFn: async ({ entityType }) => {
      const res = await axios.post(`${ADMIN_TAG_QUERY_KEY}/load-predefined`, {
        entityType,
      });
      return res.data;
    },
    onSuccess: (data, variables) => {
      setNotification({
        id: "load-predefined-tags-success",
        message: `Predefined ${variables.entityType.charAt(0).toUpperCase() + variables.entityType.slice(1)} Tags Loaded`,
        details: data.message,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [ADMIN_TAG_QUERY_KEY],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "load-predefined-tags-error",
        message: "Failed to Load Predefined Tags",
        details:
          error?.response?.data?.message ?? "Failed to load predefined tags.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
